import { z } from "zod";
import { MODES, OPS } from "../content/modes";
import { emptyProgress, newId, type PlayEvent, type Progress } from "../game/events";
import { applyEvent, foldEvents, isoDate, rankEvent, retuneDue, withSets } from "../game/results";
import {
  coerceSave,
  defaultSave,
  ProgressSchema,
  SAVE_VERSION,
  SettingsSchema,
  type SaveState,
  type Settings,
} from "./schema";

/*
  Everything this device keeps (version 3): one entry per child, the signed-in
  account if any, and how far this device has synced.

  Each child has `base`, their progress folded from events the server has
  confirmed, and `outbox`, events played here that the server hasn't confirmed
  yet. What the screens show is base with the outbox folded on top. A child
  who isn't synced (signed-out play) has no outbox: events fold straight into
  base.
*/

/** Keeps the app's original name so existing devices find their data. Never rename it. */
export const STORE_KEY = "ninja-maths-store";
export const STORE_VERSION = 3;

const iso = z.string().min(1);
const int = z.number().int().nonnegative();

const AnswerSchema = z.object({
  typeId: z.string(),
  factKey: z.string(),
  correct: z.boolean(),
  ms: z.number().int().nonnegative().nullable().catch(null),
});
const base = { id: z.string(), at: iso, day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/) };

export const PlayEventSchema = z.discriminatedUnion("kind", [
  z.object({
    ...base,
    kind: z.literal("trial"),
    mode: z.enum(MODES),
    rankIndex: int,
    score: int,
    tried: int,
    seconds: int,
    passed: z.boolean(),
    audio: z.boolean(),
    seed: int,
    /** Events queued before v1.1 have none: they were played on version 1. */
    trialsVersion: int.min(1).catch(1),
    ops: z.array(z.enum(OPS)).optional(),
    coins: int.optional(),
    durationMs: int.nullable(),
    answers: z.array(AnswerSchema),
  }),
  z.object({
    ...base,
    kind: z.literal("practice"),
    mode: z.enum(MODES),
    score: int,
    tried: int,
    seconds: int,
    audio: z.boolean(),
    ops: z.array(z.enum(OPS)).optional(),
    coins: int.optional(),
    durationMs: int.nullable(),
    answers: z.array(AnswerSchema),
  }),
  z.object({
    ...base,
    kind: z.literal("battle"),
    mode: z.enum(MODES),
    enemy: z.string(),
    fighter: z.string(),
    seconds: int,
    score: int,
    tried: int,
    points: int,
    damage: int,
    hits: int,
    won: z.boolean(),
    end: z.enum(["won", "knockout", "timeout", "retreat"]),
    moveUsed: z.boolean(),
    items: z.array(z.object({ id: z.string(), level: int })).optional(),
    itemsUsed: z.array(z.string()).optional(),
    coins: int,
    seed: int,
    audio: z.boolean(),
    battlesVersion: int.min(1),
    ops: z.array(z.enum(OPS)).optional(),
    durationMs: int.nullable(),
    answers: z.array(AnswerSchema),
  }),
  z.object({
    ...base,
    kind: z.literal("purchase"),
    item: z.string(),
    level: int.min(1),
    price: int,
    shopVersion: int.min(1),
  }),
  z.object({
    ...base,
    kind: z.literal("rank"),
    mode: z.enum(MODES),
    rankIndex: int,
    reason: z.enum(["placement", "parent", "retune"]),
    ops: z.array(z.enum(OPS)).optional(),
  }),
  z.object({ ...base, kind: z.literal("baseline"), progress: ProgressSchema }),
]);

const ChildSchema = z.object({
  id: z.string().min(1),
  name: z.string().max(20).catch(""),
  /** Picture PIN: three icon indices, or null for none. */
  pin: z.array(z.number().int().min(0).max(8)).length(3).nullable().catch(null),
  settings: SettingsSchema.catch(defaultSave().settings),
  /** When name, PIN or settings last changed (last writer wins across devices). */
  updatedAt: iso.catch(new Date(0).toISOString()),
  /** Needs sending to the server. */
  dirty: z.boolean().catch(false),
  /** Selected strand on this device. */
  mode: z.enum(MODES).catch("add"),
  onboarded: z.boolean().catch(false),
  /** Strands given the one-off retune award on this device, to celebrate on Home. */
  celebrate: z.array(z.enum(MODES)).catch([]),
  /** Exists on the server and syncs. */
  synced: z.boolean().catch(false),
  removed: z.boolean().catch(false),
  base: ProgressSchema.catch(emptyProgress()),
  outbox: z
    .array(z.unknown())
    .catch([])
    .transform((rows) =>
      rows.flatMap((r) => {
        const p = PlayEventSchema.safeParse(r);
        return p.success ? [p.data as PlayEvent] : [];
      }),
    ),
});
export type LocalChild = z.output<typeof ChildSchema>;

const AccountSchema = z.object({
  userId: z.string(),
  email: z.string(),
  familyId: z.string(),
});
export type Account = z.output<typeof AccountSchema>;

const StoreSchema = z.object({
  version: z.literal(STORE_VERSION).catch(STORE_VERSION),
  deviceId: z
    .string()
    .min(1)
    .catch(() => newId()),
  account: AccountSchema.nullable().catch(null),
  /** Highest server seq folded into every child's base. */
  cursor: int.catch(0),
  lastSync: iso.nullable().catch(null),
  activeChildId: z.string().nullable().catch(null),
  children: z
    .array(z.unknown())
    .catch([])
    .transform((rows) =>
      rows.flatMap((r) => {
        const p = ChildSchema.safeParse(r);
        return p.success ? [p.data] : [];
      }),
    ),
});
export type Store = z.output<typeof StoreSchema>;

export function newChild(name: string, settings: Settings, now = new Date()): LocalChild {
  return {
    id: newId(),
    name: name.trim().slice(0, 20).trim(),
    pin: null,
    settings,
    updatedAt: now.toISOString(),
    dirty: true,
    mode: "add",
    onboarded: false,
    celebrate: [],
    synced: false,
    removed: false,
    base: emptyProgress(),
    outbox: [],
  };
}

export function progressOf(save: SaveState): Progress {
  const {
    rank,
    trials,
    history,
    mastery,
    missed,
    stripes,
    sets,
    overall,
    retuned,
    coins,
    roster,
    spent,
    items,
  } = save;
  return {
    rank,
    trials,
    history,
    mastery,
    missed,
    stripes,
    sets,
    overall,
    retuned,
    coins,
    roster,
    spent,
    items,
  };
}

/** A local child made from a single save (signed-out play, an older version, an import). */
export function childFromSave(save: SaveState, now = new Date()): LocalChild {
  return {
    ...newChild(save.name, save.settings, now),
    dirty: false,
    mode: save.mode,
    onboarded: save.onboarded,
    base: progressOf(save),
  };
}

export function emptyStore(): Store {
  const child = childFromSave(defaultSave());
  return {
    version: STORE_VERSION,
    deviceId: newId(),
    account: null,
    cursor: 0,
    lastSync: null,
    activeChildId: child.id,
    children: [child],
  };
}

/** Turn anything into a valid store. Never throws. Keeps at least one child. */
export function coerceStore(raw: unknown): Store {
  const parsed = StoreSchema.parse(raw && typeof raw === "object" ? raw : {});
  const live = parsed.children.filter((c) => !c.removed);
  if (live.length === 0) {
    const child = childFromSave(defaultSave());
    return { ...parsed, children: [...parsed.children, child], activeChildId: child.id };
  }
  const active = live.some((c) => c.id === parsed.activeChildId)
    ? parsed.activeChildId
    : live[0]!.id;
  return { ...parsed, activeChildId: active };
}

/** A store from a version 1 or 2 save: that save becomes the only child. */
export function storeFromSave(raw: unknown): Store {
  const child = childFromSave(coerceSave(raw));
  return { ...emptyStore(), activeChildId: child.id, children: [child] };
}

// ─── Reading ────────────────────────────────────────────────────────────────

export function liveChildren(store: Store): LocalChild[] {
  return store.children.filter((c) => !c.removed);
}

/** The child playing now. Never undefined: a store with no children gets a fresh one. */
export function activeChild(store: Store): LocalChild {
  const live = liveChildren(store);
  return live.find((c) => c.id === store.activeChildId) ?? live[0] ?? STARTER;
}

const STARTER = childFromSave(defaultSave());

/** What the screens show for a child: base with unconfirmed events on top. */
export function progressFor(child: LocalChild): Progress {
  return foldEvents(child.base, child.outbox);
}

/** A child as the single-save shape the game screens use. */
/**
 * A child as the single-save shape the game screens use. Gold and foil cards
 * include any the strands switched on today complete; they're recorded for
 * good with the next thing the child plays.
 */
export function viewOf(child: LocalChild, today = isoDate()): SaveState {
  const { progress } = withSets(progressFor(child), child.settings.ops, today);
  return {
    version: SAVE_VERSION,
    name: child.name,
    mode: child.mode,
    settings: child.settings,
    onboarded: child.onboarded,
    ...progress,
  };
}

// ─── Changing ───────────────────────────────────────────────────────────────

export function updateChild(store: Store, id: string, f: (c: LocalChild) => LocalChild): Store {
  return { ...store, children: store.children.map((c) => (c.id === id ? f(c) : c)) };
}

/**
 * Record something a child did. Synced children queue it for the server;
 * others fold it in straight away.
 */
export function appendEvent(store: Store, childId: string, event: PlayEvent): Store {
  return updateChild(store, childId, (c) =>
    c.synced ? { ...c, outbox: [...c.outbox, event] } : { ...c, base: applyEvent(c.base, event) },
  );
}

/** Change a child's name, PIN or settings, marking it for sync. */
export function editChild(
  store: Store,
  id: string,
  patch: Partial<Pick<LocalChild, "name" | "pin" | "settings">>,
  now = new Date(),
): Store {
  return updateChild(store, id, (c) => ({
    ...c,
    ...patch,
    ...(patch.name !== undefined ? { name: patch.name.trim().slice(0, 20).trim() } : {}),
    updatedAt: now.toISOString(),
    dirty: true,
  }));
}

/** Events waiting for the server, across every child, oldest first per child. */
export function pendingCount(store: Store): number {
  return store.children.reduce(
    (n, c) => n + c.outbox.length + (c.dirty && store.account ? 1 : 0),
    0,
  );
}

/**
 * The one-off v1.1 award: for each child, any strand whose best on the next
 * trial already meets the new, lower pass mark gets a "retune" rank event,
 * and a celebration on this device. Checked at load and after each sync, so
 * results that arrive late still count. The fold makes it happen at most once
 * per strand, however many devices send it.
 */
export function withRetunes(store: Store, now = new Date()): Store {
  let next = store;
  for (const c of liveChildren(store)) {
    const p = progressFor(c);
    const due = MODES.filter((m) => retuneDue(p, m));
    if (!due.length) continue;
    for (const m of due)
      next = appendEvent(next, c.id, rankEvent(m, p.rank[m] + 1, "retune", c.settings.ops, now));
    next = updateChild(next, c.id, (x) => ({
      ...x,
      celebrate: [...new Set([...x.celebrate, ...due])],
    }));
  }
  return next;
}
